import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useCMS } from "../lib/cms";

const NAV_DEFAULTS = {
  items: [
    { to: "/",                label: "Home" },
    { to: "/about",           label: "About" },
    { to: "/properties",      label: "Stays" },
    { to: "/long-stay",       label: "Long Stay" },
    { to: "/why-choose-us",   label: "Hammersmith" },
    { to: "/contact",         label: "Concierge" },
  ],
  cta_label: "Book Your Stay",
};
const BRAND_DEFAULTS = {
  logo_url: "/brand/wls-logo.png",
  wordmark: "West London Stays",
  subline: "Hammersmith, W6",
};

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  // CMS — read with defaults
  const navCMS = useCMS("navigation", NAV_DEFAULTS);
  const brand = useCMS("brand", BRAND_DEFAULTS);
  // Backend stores nav items as { label, href } — normalise to internal { label, to } shape.
  const NAV_ITEMS = (navCMS.items || NAV_DEFAULTS.items).map((it) => ({
    label: it.label,
    to: it.to || it.href || "/",
  }));
  const ctaLabel = navCMS.cta_label || NAV_DEFAULTS.cta_label;

  const transparentByDefault = pathname === "/";
  const transparent = transparentByDefault && !scrolled && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [pathname]);

  const headerCls = transparent
    ? "bg-transparent"
    : "bg-[rgba(244,239,227,0.92)] backdrop-blur-md border-b border-[var(--ed-rule)] shadow-[0_2px_22px_-14px_rgba(20,17,12,0.3)]";

  const linkInk = transparent ? "text-white/95" : "text-[var(--ed-ink)]";
  const linkActive = transparent
    ? "text-white border-white"
    : "text-[var(--ed-emerald)] border-[var(--ed-emerald)]";

  const wordmarkColor = transparent ? "#FFFFFF" : "var(--ed-ink)";
  const sublineColor = transparent ? "rgba(255,255,255,0.78)" : "var(--ed-ink-muted)";

  return (
    <header
      data-testid="main-navbar"
      data-transparent={transparent ? "true" : "false"}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerCls}`}
    >
      <div className="ed-container flex items-center justify-between gap-6 py-4 lg:py-5">
        {/* Brand block — logo square + WEST LONDON STAYS / HAMMERSMITH, W6 */}
        <Link to="/" data-testid="navbar-brand" className="flex items-center gap-3 lg:gap-4 group flex-shrink-0">
          <img
            data-testid="nav-logo"
            src={brand.logo_url || "/brand/wls-logo.png"}
            alt={brand.wordmark || "West London Stays"}
            className="h-14 sm:h-16 lg:h-[72px] w-auto object-contain"
            style={{
              filter: transparent
                ? "drop-shadow(0 4px 18px rgba(0,0,0,0.55))"
                : "drop-shadow(0 2px 8px rgba(0,0,0,0.18))",
            }}
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span
              data-testid="nav-wordmark"
              className="font-serif text-[0.95rem] lg:text-[1.05rem] tracking-[0.34em] uppercase transition-colors duration-500"
              style={{ color: wordmarkColor }}
            >
              {brand.wordmark || "West London Stays"}
            </span>
            <span
              className="block w-full h-px my-2 transition-colors duration-500"
              style={{ backgroundColor: transparent ? "rgba(255,255,255,0.55)" : "var(--ed-rule)" }}
            />
            <span
              data-testid="nav-subline"
              className="font-sans text-[0.62rem] tracking-[0.36em] uppercase transition-colors duration-500"
              style={{ color: sublineColor }}
            >
              {brand.subline || "Hammersmith, W6"}
            </span>
          </div>
        </Link>

        {/* Centred nav — reference layout */}
        <nav className="hidden lg:flex flex-1 items-center justify-center gap-9">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[0.72rem] tracking-[0.32em] uppercase font-medium transition-all duration-300 pb-1 border-b ${
                  isActive ? linkActive : `${linkInk} border-transparent hover:border-current`
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 flex-shrink-0">
          {pathname === "/long-stay" ? (
            <a
              href="#enquiry"
              data-testid="nav-cta-book"
              className="hidden lg:inline-flex ed-btn-primary"
              style={{ padding: "0.85rem 1.7rem", fontSize: "0.72rem" }}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              Request availability
            </a>
          ) : (
            <Link
              to="/properties"
              data-testid="nav-cta-book"
              className="hidden lg:inline-flex ed-btn-primary"
              style={{ padding: "0.85rem 1.7rem", fontSize: "0.72rem" }}
            >
              {ctaLabel}
            </Link>
          )}

          <button
            data-testid="navbar-menu-toggle"
            className={`lg:hidden p-2 ${transparent ? "text-white" : "text-[var(--ed-ink)]"}`}
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-nav" className="lg:hidden bg-[var(--ed-bg)] border-t border-[var(--ed-rule)]">
          <div className="ed-container py-6 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 border-b border-[var(--ed-rule-soft)] flex items-center justify-between ${
                    isActive ? "text-[var(--ed-emerald)]" : "text-[var(--ed-ink)]"
                  }`
                }
              >
                <span className="font-serif text-xl">{item.label}</span>
                <span className="ed-overline" style={{ fontSize: "0.6rem" }}>→</span>
              </NavLink>
            ))}
            {pathname === "/long-stay" ? (
              <a
                href="#enquiry"
                onClick={(e) => {
                  e.preventDefault();
                  setOpen(false);
                  setTimeout(() => document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
                }}
                data-testid="mobile-cta-book"
                className="ed-btn-primary mt-6"
              >
                Request availability
              </a>
            ) : (
              <Link
                to="/properties"
                onClick={() => setOpen(false)}
                data-testid="mobile-cta-book"
                className="ed-btn-primary mt-6"
              >
                {ctaLabel}
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
